<template>
  <section>
    <!-- Blocks the page while the file is uploading, so a second drop can't start a competing import. -->
    <z-overlay v-if="isUploading" notice="uploading" custom-style="position: fixed;" @cancel="cancelUpload" />
    <div class="row mt-2">
      <div class="col">
        <p>
          Use this facility to import <b>OTHER</b> matrix related data. This is <u>rarely used</u> as this data is already set up within the ADAS Map
          Matrix.
        </p>
        <p>
          However you can use this to perform updates and additions. <u>First</u>, you need to choose what data you'll be importing. Then, drag and
          drop your comma-delimited file onto the grey area below.
        </p>
        <p>
          Depending upon the data you're uploading, the file needs to be in the proper format for processing. You can use the following sample file
          links as a guide.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <ul class="custom-list">
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-systems.csv?v=1" target="_blank">Systems Here</a>
          </li>
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-locations.csv" target="_blank">Locations Here</a>
          </li>
        </ul>
      </div>
      <div class="col">
        <ul class="custom-list">
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-checklist-categories.csv" target="_blank"
              >Matrix Categories Here</a
            >
          </li>
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-checklist-sub-categories.csv" target="_blank"
              >Matrix Sub-Categories Here</a
            >
          </li>
        </ul>
      </div>
      <div class="col">
        <ul class="custom-list">
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-info-lookups.csv" target="_blank">Info Links Here</a>
          </li>
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-calibration-services.csv" target="_blank"
              >Calibration Services Here</a
            >
          </li>
        </ul>
      </div>
      <div class="col">
        <ul class="custom-list">
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-exclusions.csv" target="_blank">Exclusions Here</a>
          </li>
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-report-recommendations.csv" target="_blank"
              >Report Recommendations Here</a
            >
          </li>
        </ul>
      </div>
      <div class="col">
        <ul class="custom-list">
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-noscrublist.csv" target="_blank">No Scrub List Here</a>
          </li>
          <li>
            <a href="https://opusbroker.blob.core.windows.net/adasmatrix/samples/matrix-calibration-pricing.csv" target="_blank"
              >Calibration Pricing Here</a
            >
          </li>
        </ul>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-3">
        <z-select v-model="dataChoice" label="Process" :options="choices" :inline="true" :disabled="isBusy" @change="selectedChoice" />
      </div>
      <div class="col">
        <file-pond
          v-show="isSelected"
          ref="pond"
          name="test"
          :server="{ process, revert, restore, load, fetch }"
          :label-idle="isBusy ? 'Import in progress...' : 'Drop *.CSV file here...'"
          allow-multiple="true"
          accepted-file-types="text/plain"
          :disabled="isBusy"
          :files="uploadFiles"
          @init="handleFilePondInit"
          @updatefiles="handleFilePondAdd"
        />
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <span class="custom-message">
          <FontAwesomeIcon v-if="isImporting" icon="fa-solid fa-spinner me-2" spin /><span>{{ message }}</span></span
        >
        <!-- Escape hatch: the progress feed can stall, and without this the form stays disabled until a reload. -->
        <button v-if="isImporting" class="btn btn-link btn-sm custom-unlock" @click="unlock">not responding? re-enable this form</button>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-3">
        <button v-if="isSelected" class="btn btn-primary btn-sm" :disabled="isDownloading || isBusy" @click="exportData">
          <FontAwesomeIcon v-if="isDownloading" icon="fa-solid fa-spinner me-2" spin />
          Export {{ chosen }}
        </button>
      </div>
      <div class="col">
        <div v-html="importMessage"></div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { adasmatrixStore } from '@/stores/adasmatrix'
import ZSelect from '@/components/utilities/elements/Select.vue'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import axios from 'axios'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.css'
const FilePond = vueFilePond()
export default {
  name: 'MatrixImportOthers',
  components: {
    FilePond,
    ZSelect,
    ZOverlay,
    FontAwesomeIcon
  },
  // Let the host lock its tab strip while this screen is busy.
  emits: ['busy'],
  watch: {
    isBusy(value) {
      this.$emit('busy', value)
    }
  },
  data() {
    return {
      firm: 1,
      isImporting: false,
      isUploading: false,
      uploadSource: null,
      uploadFiles: [],
      apiUrl: '',
      message: null,
      choices: [
        {
          value: 1,
          name: 'Systems'
        },
        {
          value: 2,
          name: 'Locations'
        },
        {
          value: 3,
          name: 'Matrix Categories'
        },
        {
          value: 4,
          name: 'Matrix Sub-Categories'
        },
        {
          value: 5,
          name: 'ADAS Info Links'
        },
        {
          value: 6,
          name: 'Calibration Services'
        },
        {
          value: 7,
          name: 'Exclusions'
        },
        {
          value: 8,
          name: 'Report Recommendations'
        },
        {
          value: 9,
          name: 'No Scrub List'
        },
        {
          value: 10,
          name: 'Calibration Pricing'
        }
      ],
      dataChoice: null,
      isSelected: false,
      isDownloading: false,
      chosen: null,
      importMessage: null
    }
  },
  mounted() {
    this.isSelected = false
    this.isImporting = false
    this.isUploading = false
    this.isDownloading = false
    this.dataChoice = null
    this.chosen = null
    this.importMessage = null
    this.apiUrl = window.config ? window.config.apiUrl : ''
  },
  computed: {
    // Uploading or importing: either way the form must not accept another submission.
    isBusy() {
      return this.isUploading || this.isImporting
    },
    ...mapState(authStore, ['token']),
    ...mapState(adasmatrixStore, [
      'matrixAdasInfo',
      'locations',
      'systems',
      'calibrations',
      'matrixAdas',
      'matrixAdasItems',
      'exclusions',
      'reportRecommendations',
      'noScrubList',
      'calibrationPricing'
    ])
  },
  methods: {
    ...mapActions(adasmatrixStore, [
      'importMatrixOthers',
      'checklistMatrixAdasInfoExport',
      'systemsExport',
      'locationsExport',
      'calibrationsExport',
      'checklistMatrixAdasExport',
      'checklistMatrixItemExport',
      'exclusionsExport',
      'recommendationsExport',
      'noScrubListExport',
      'calibrationPricingExport'
    ]),
    async importData(data) {
      let path = data.data.path
      let filename = data.data.filename
      this.isImporting = true
      console.log('Uploaded File', {
        path: path,
        filename: filename
      })
      this.$refs.pond.removeFiles({ revert: true })
      this.message = 'upload complete, processing...'
      await this.importMatrixOthers({
        path: path,
        filename: filename,
        choice: this.dataChoice
      })
      this.complete()
    },
    complete() {
      this.isImporting = false
      this.message = 'import complete'
    },
    // Dismiss the upload overlay by cancelling the request behind it.
    cancelUpload() {
      if (this.uploadSource) {
        this.uploadSource.cancel('Operation canceled by the user.')
      }
      this.isUploading = false
      this.message = 'upload cancelled'
    },
    // Release the form when the progress feed has stopped reporting.
    unlock() {
      this.isImporting = false
      this.message = 'form re-enabled - the import may still be running on the server'
    },
    process(fieldName, file, metadata, load, error, progress) {
      let $this = this
      this.message = null
      this.isUploading = true
      // set data
      const formData = new FormData()
      formData.append('files', file, file.name)
      formData.append('firm', this.firm)
      formData.append('preference', 'MatrixOthers')
      formData.append('path', `adasmatrix/${this.firm}`)
      // related to aborting the request
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      this.uploadSource = source
      axios.interceptors.request.use(
        (config) => {
          config.headers['Authorization'] = 'Bearer ' + this.token
          return config
        },
        (error) => {
          return Promise.reject(error)
        }
      )
      // the request itself
      axios({
        method: 'post',
        url: `${this.apiUrl}/data/uploadFile`,
        data: formData,
        cancelToken: source.token,
        onUploadProgress: (e) => {
          // updating progress indicator
          progress(e.lengthComputable, e.loaded, e.total)
        }
      })
        .then((response) => {
          // Hand over from the blocking overlay to the disabled-form state.
          $this.isUploading = false
          $this.importData(response)
        })
        .catch((thrown) => {
          // Always clear the overlay, or a failed upload leaves the page blocked.
          $this.isUploading = false
          if (axios.isCancel(thrown)) {
            console.log('Request canceled', thrown.message)
          } else {
            console.log('Upload failed', thrown)
            $this.message = 'upload failed, nothing was imported - please try again'
          }
        })
      // Setup abort interface
      return {
        abort: () => {
          source.cancel('Operation canceled by the user.')
          $this.isUploading = false
        }
      }
    },
    handleError(error) {
      console.log('Error', error)
    },
    handleFilePondInit: function () {},
    handleFilePondAdd: function () {},
    selectedChoice() {
      let choice = null
      this.chosen = null
      this.importMessage = null
      this.isSelected = this.dataChoice !== null
      if (this.isSelected) {
        choice = this.choices.find((element) => parseInt(element.value) === parseInt(this.dataChoice))
        console.log('Selected Choice', choice)
        this.chosen = choice.name
      }
      if (choice.value === 10) {
        // Calibration Pricing
        this.importMessage = `<b>NOTE:</b> when updating pricing, set the 'IsInactive' flag as <i>TRUE</i> in the *.csv file. This will deactivate the current pricing and replace with your new pricing.`
      }
    },
    async exportData() {
      this.isDownloading = false
      console.log('Exporting Data', {
        selected: this.isSelected,
        choice: this.dataChoice
      })
      if (this.isSelected) {
        switch (parseInt(this.dataChoice)) {
          case 1:
            await this.exportSystems()
            break
          case 2:
            await this.exportLocations()
            break
          case 3:
            await this.exportMatrixCategories()
            break
          case 4:
            await this.exportMatrixSubCategories()
            break
          case 5:
            await this.exportInfoItems()
            break
          case 6:
            await this.exportCalibrationServices()
            break
          case 7:
            await this.exportExclusions()
            break
          case 8:
            await this.exportReportRecommendations()
            break
          case 9:
            await this.exportNoScrubList()
            break
          case 10:
            await this.exportCalibrationPricing()
            break
          default:
            break
        }
      }
    },
    async exportCalibrationPricing() {
      console.log('Exporting Calibration Pricing')
      this.isDownloading = true
      await this.calibrationPricingExport()
      if (this.calibrationPricing) {
        var blob = new Blob([this.calibrationPricing], { type: 'text/csv' })
        this.transformData(blob, 'Calibration Pricing')
      }
      this.isDownloading = false
    },
    async exportNoScrubList() {
      console.log('Exporting No Scrub List')
      this.isDownloading = true
      await this.noScrubListExport()
      if (this.noScrubList) {
        var blob = new Blob([this.noScrubList], { type: 'text/csv' })
        this.transformData(blob, 'No Scrub List')
      }
      this.isDownloading = false
    },
    async exportReportRecommendations() {
      console.log('Exporting Report Recommendations')
      this.isDownloading = true
      await this.recommendationsExport(false)
      if (this.reportRecommendations) {
        var blob = new Blob([this.reportRecommendations], { type: 'text/csv' })
        this.transformData(blob, 'Recommendations')
      }
      this.isDownloading = false
    },
    async exportExclusions() {
      console.log('Exporting Exclusions')
      this.isDownloading = true
      await this.exclusionsExport(false)
      if (this.exclusions) {
        var blob = new Blob([this.exclusions], { type: 'text/csv' })
        this.transformData(blob, 'Exclusions')
      }
      this.isDownloading = false
    },
    async exportSystems() {
      this.isDownloading = true
      await this.systemsExport(false)
      if (this.systems) {
        var blob = new Blob([this.systems], { type: 'text/csv' })
        this.transformData(blob, 'Systems')
      }
      this.isDownloading = false
    },
    async exportLocations() {
      this.isDownloading = true
      await this.locationsExport(false)
      if (this.locations) {
        var blob = new Blob([this.locations], { type: 'text/csv' })
        this.transformData(blob, 'Locations')
      }
      this.isDownloading = false
    },
    async exportCalibrationServices() {
      this.isDownloading = true
      await this.calibrationsExport(false)
      if (this.calibrations) {
        var blob = new Blob([this.calibrations], { type: 'text/csv' })
        this.transformData(blob, 'Calibration Services')
      }
      this.isDownloading = false
    },
    async exportMatrixCategories() {
      //checklistMatrixAdasExport
      this.isDownloading = true
      await this.checklistMatrixAdasExport(false)
      if (this.matrixAdas) {
        var blob = new Blob([this.matrixAdas], { type: 'text/csv' })
        this.transformData(blob, 'Matrix Checklist Categories')
      }
      this.isDownloading = false
    },
    async exportMatrixSubCategories() {
      console.log('Exporting ADAS Matrix Items')
      this.isDownloading = true
      await this.checklistMatrixItemExport({
        source: 4,
        isInactive: false,
        vehicleMake: null
      })
      if (this.matrixAdasItems) {
        var blob = new Blob([this.matrixAdasItems], { type: 'text/csv' })
        this.transformData(blob, 'Matrix Checklist Sub-Categories')
      }
      this.isDownloading = false
    },
    async exportInfoItems() {
      console.log('Exporting ADAS Info Links')
      this.isDownloading = true
      await this.checklistMatrixAdasInfoExport({
        source: 4,
        isInactive: false,
        vehicleMake: null
      })
      if (this.matrixAdasInfo) {
        var blob = new Blob([this.matrixAdasInfo], { type: 'text/csv' })
        this.transformData(blob, 'Info Links')
      }
      this.isDownloading = false
    },
    transformData(blob, type) {
      var link = document.createElement('a')
      var now = moment().format('MMDDYYYY')
      var filename = type ? `ADAS MAP ${type} ${now}.csv` : `ADAS MAP Info Links ${now}.csv`
      if (window.URL.createObjectURL) {
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        link.click()
      } else {
        this.message = 'Refresh your browser to generate this download.'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-message {
  margin-left: 15px;
  margin-right: 15px;
  font-size: 0.9em;
}
.custom-list {
  list-style-type: none;
  padding: 0;
  margin: 15px;
}
.custom-unlock {
  font-size: 0.8em;
  padding: 0;
  vertical-align: baseline;
}
</style>
